/// <amd-module name="VSSUI/Components/ResponsiveViewport/ResponsiveViewport" />

import * as React from "react";
import { EventManagement } from "VSS/Core/EventManagement";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { css } from "VSSUI/Util";
import { IBreakPoint, IResponsiveViewportProps, IResponsiveViewport } from "../../Components/ResponsiveViewport/ResponsiveViewport.Props";

export interface IResponsiveWrapperState<T extends IBreakPoint = IBreakPoint> {
    /** Currently active breakpoints */
    activeBreakpoints: T[];
}

const ResizeDelayMs = 100;

export class ResponsiveViewport<T extends IBreakPoint = IBreakPoint> extends React.Component<IResponsiveViewportProps<T>, IResponsiveWrapperState<T>>
    implements IResponsiveViewport {
    private _element: HTMLElement;
    private events: EventManagement;
    private timers: TimerManagement;

    constructor(props: IResponsiveViewportProps<T>) {
        super(props);

        const { initialWidth } = props;
        this.events = new EventManagement();
        this.timers = new TimerManagement();

        this.state = {
            activeBreakpoints: initialWidth == null ? [] : this._getActiveBreakpoints(initialWidth)
        };

        this._onAsyncResize = this.timers.throttle(this._onAsyncResize, ResizeDelayMs, {
            leading: false
        });
    }

    componentDidMount() {
        this.events.addEventListener(window, "resize", this._onAsyncResize);

        // React to initial width
        this._onAsyncResize();
    }

    componentWillUnmount() {
        this.events.removeAllListeners();
        this.timers.dispose();
    }

    shouldComponentUpdate(newProps: IResponsiveViewportProps<T>, newState: IResponsiveWrapperState<T>): boolean {
        if (this.props !== newProps) {
            return true;
        }

        const newBreakPoints = newState.activeBreakpoints;
        const oldBreakPoints = this.state.activeBreakpoints;
        return newBreakPoints.length !== oldBreakPoints.length || newBreakPoints.some((b: T, idx: number) => b !== oldBreakPoints[idx]);
    }

    render() {
        const { tag = "div", className, children, onRenderContent } = this.props;
        const { activeBreakpoints } = this.state;

        const content = (onRenderContent && onRenderContent(activeBreakpoints)) || children;

        return React.createElement(
            tag as "span",
            {
                className: css(className, ...(activeBreakpoints && activeBreakpoints.map(x => x.className))),
                ref: this._resolveElement
            },
            content
        );
    }

    measure() {
        this._resize();
    }

    private _onAsyncResize = () => {
        this._resize();
    };

    private _resize() {
        const rect = this._element.getBoundingClientRect();
        const { width } = rect;

        this.setState({
            activeBreakpoints: this._getActiveBreakpoints(width)
        });
    }

    private _getActiveBreakpoints(width: number): T[] {
        const { breakPoints } = this.props;

        return breakPoints.filter(bp => {
            if (bp.minWidth >= 0 && width < bp.minWidth) {
                return false;
            }

            if (bp.maxWidth >= 0 && width >= bp.maxWidth) {
                return false;
            }

            return true;
        });
    }

    private _resolveElement = (element: HTMLElement) => {
        this._element = element;
    };
}
